import { Component, effect, inject, input, output, signal } from '@angular/core';
import { SpacesService } from 'account-hybrid/common/components/spaces';
import { IdentityService, SessionService } from 'account-hybrid/core/authentication';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';
import { buildAzureStoredPhotoUrl } from 'apps/account/src/account-shared/pipes/azure-stored-photo.util';
import { NativeCurationModalService } from 'account-hybrid/common/components/curation-modal/native/native-curation-modal.service';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs/esm/index';
import utc from 'dayjs/esm/plugin/utc';
import { TimelineCurationService } from '../../curation/services/timeline-curation.service';
import { AdvocatesApiService } from '../services/advocates-api.service';

dayjs.extend(utc);

/**
 * Native-Angular port of the BODY of the AngularJS people guest-detail modal
 * (account-v1/.../people/details/details.controller.js + details.html). The
 * outer ftModal shell stays AngularJS for now (account-v2 details.component
 * still opens/closes it via ftModal.show/hide) — this component renders only
 * the modal's transcluded content. Downgraded as `ftPeopleDetailsBody` so
 * the AngularJS shell can host it; full ftModal upgrade is M5b/M6.
 *
 * Admin actions (save / cancelReservation / sendCertificate / curate /
 * editCertificateExpirationDate / requestPromotionCertificate) are stubbed —
 * the parity gate captures the modal's INITIAL LOAD state (no admin
 * interaction), and the sub-modals stay hidden behind the unported ftModal
 * directive. Wire each action when the corresponding flow needs gating.
 */
@Component({
    selector: 'ft-people-details-body',
    templateUrl: './details-body.component.html',
    standalone: false
})
export class PeopleDetailsBodyComponent {
    readonly advocateUuid = input.required<string>();
    readonly curate = input(false);

    /** Emitted after the advocate loads, so the native DetailsComponent shell can
     *  render the header (`{{ advocate | fullName:!isPiiInfoVisible }}`) without a
     *  second advocate fetch (v1 ran one purely for the header). */
    readonly loaded = output<{ advocate: any; isPiiInfoVisible: boolean }>();

    protected readonly advocate = signal<any>(undefined);
    protected readonly isLoading = signal(true);
    /** "Reveal PII" toggle — starts hidden when any PII is present, like v1. */
    protected readonly isPiiInfoVisible = signal(false);
    /** Drives the native "Edit guest info" modal (v1 `people.details.guestInfo`). */
    protected readonly editOpen = signal(false);
    /** Drives the native "Remove details" modal (v1 `people.details.cancelReservation`). */
    protected readonly removeOpen = signal(false);
    /** Drives the certificate expiration-date edit modal (v1 `advocateEditCertificateExpirationDate`). */
    protected readonly expirationOpen = signal(false);
    selectedIncentive: any;
    removingProfileImage = false;
    readonly deleteReasons = [
        { value: 'Guest canceled reservation' },
        { value: 'Test user' },
        { value: 'Other' },
    ];
    /** Cache-buster on photo URLs — set once on the FIRST render, like v1's `vm.timestamp = new Date().getTime()`. */
    readonly timestamp = new Date().getTime();

    private readonly advocates = inject(AdvocatesApiService);
    private readonly spaces = inject(SpacesService);
    protected readonly identity = inject(IdentityService);
    protected readonly environment = inject<Environment>(ENVIRONMENT_TOKEN);
    private readonly session = inject(SessionService);
    private readonly nativeCurationModal = inject(NativeCurationModalService);
    private readonly timelineCuration = inject(TimelineCurationService);
    private readonly translate = inject(TranslateService);

    constructor() {
        // Re-load if the modal is reused for a new advocate without a fresh mount.
        effect(() => {
            this.load();
        });
    }

    protected isHotelStayAvailable(): boolean {
        const advocate = this.advocate();
        return !!(advocate && advocate.epochStartDate && advocate.epochEndDate);
    }
    protected isInvitee(): boolean {
        const advocate = this.advocate();
        return !!(advocate && advocate.logTypeId == 11);
    }
    protected isPrestayAvailable(): boolean {
        const advocate = this.advocate();
        return !!(advocate && advocate.prestay);
    }
    protected isPoststayAvailable(): boolean {
        const advocate = this.advocate();
        return !!(advocate && advocate.poststay);
    }
    protected isPhotoContestAvailable(): boolean {
        const advocate = this.advocate();
        return !!(
            advocate &&
            advocate.photoContest &&
            this.isPoststayAvailable() &&
            advocate.poststay?.photo &&
            this.parseBool(advocate.poststay.photo.isFinalist)
        );
    }

    protected showPiiInfo(): void {
        this.isPiiInfoVisible.set(true);
    }

    /** guestInfo edit modal saved → reflect changes in the body + refresh the shell header
     *  (v1 `updateSheetTitle` broadcast). */
    protected onGuestSaved(updated: any): void {
        this.advocate.set(updated);
        this.loaded.emit({ advocate: updated, isPiiInfoVisible: this.isPiiInfoVisible() });
    }

    // TODO(M5b): wire admin actions to AdvocatesApiService.save / .delete /
    // CertificateApiService.send / .update + the sub-modal show/hide. The legacy
    // AngularJS controller methods live on the wrapper `ftPeopleDetails` state
    // component, which the modal already hosts.
    save(): void {}
    cancelReservation(): void {}
    // Resend an incentive certificate email — v1 `vm.sendCertificate` (Certificate.send + alert).
    protected sendCertificate(incentive: any): void {
        this.timelineCuration
            .sendEmailCertificate(this.spaces.getPropertyUuid()!, this.advocateUuid(), incentive.voucherUUID)
            .subscribe({
                next: () => window.alert(this.translate.instant('flipto.account.SendCertificateSuccessfull')),
                error: () => window.alert(this.translate.instant('flipto.account.SendCertificateFailed')),
            });
    }
    // Opens the native curation modal for this advocate — verbatim port of v1
    // people-details `vm.curate` (details.controller.js): `curationModal.new(property,
    // advocateUUID, false)` — isShowTimeline FALSE (people-details hides the timeline,
    // unlike story-modal's openFromStoryFlow which passes true). Was a stub from the
    // M5b body port (parity gate only captured initial load).
    protected curateModal(): void {
        const property = this.session.getProperty(this.spaces.getPropertyUuid());
        this.nativeCurationModal.new(property, this.advocateUuid(), false);
    }
    // v1 `vm.editCertificateExpirationDate`: select the incentive + open the expiration modal.
    protected editCertificateExpirationDate(incentive: any): void {
        this.selectedIncentive = incentive;
        this.expirationOpen.set(true);
    }
    // v1 `vm.saveCertificateExpirationDate` (Certificate.update → recompute isExpired).
    protected saveCertificateExpirationDate(): void {
        this.timelineCuration
            .saveCertificateExpirationDate(this.spaces.getPropertyUuid()!, this.advocateUuid(), this.selectedIncentive.voucherUUID, {
                ExpirationDate: this.selectedIncentive.utcExpirationDate,
            })
            .subscribe({
                next: () => {
                    this.selectedIncentive.isExpired = dayjs(this.selectedIncentive.utcExpirationDate).isBefore(dayjs().utc());
                    this.expirationOpen.set(false);
                },
            });
    }
    goToDirectory(): void {}
    hideCancelModal(): void {}
    saveCanceled(): void {}
    // v1 `vm.requestPromotionCertificate` (Advocates.requestPromotionCertificate).
    protected requestPromotionCertificate(): void {
        const advocate = this.advocate();
        this.advocates
            .requestPromotionCertificate(this.spaces.getPropertyUuid()!, advocate?.companyCode, advocate?.guestUUID)
            .subscribe();
    }

    /** Template helper for the AngularJS `parse: 'bool'` filter. */
    private parseBool(v: any): boolean {
        if (typeof v === 'boolean') return v;
        if (v === 1 || v === '1' || v === 'true') return true;
        return false;
    }

    private load(): void {
        if (!this.advocateUuid()) return;
        this.isLoading.set(true);
        this.advocates.details(this.spaces.getPropertyUuid()!, this.advocateUuid()).subscribe({
            next: (data) => {
                const advocate: any = data;
                this.isLoading.set(false);
                // Photo URL post-processing (account-v1 did this in the controller). Native
                // ftAzureStoredPhoto (buildAzureStoredPhotoUrl) — identical CDN URLs to the baseline.
                const azurePhoto = (photo: any) =>
                    buildAzureStoredPhotoUrl(photo, 'small', this.environment.AzureCdnBaseUrl);
                if (advocate.poststay?.photo) {
                    advocate.poststay.photo.photoUrl =
                        azurePhoto(advocate.poststay.photo) + `?timestamp=${this.timestamp}`;
                }
                if (advocate.photoContest) {
                    for (const photo of advocate.photoContest.photos ?? []) {
                        photo.photoUrl = azurePhoto(photo) + `?timestamp=${this.timestamp}`;
                    }
                }
                if (!advocate.profileImageUrl) advocate.profileImageUrl = '';
                this.advocate.set(advocate);
                // PII visibility starts hidden if any PII present (matches v1).
                const hasPii = !!(advocate.reservationNumber || advocate.email || advocate.twitter);
                this.isPiiInfoVisible.set(!hasPii);
                this.loaded.emit({ advocate, isPiiInfoVisible: this.isPiiInfoVisible() });
            },
            error: () => {
                this.isLoading.set(false);
            },
        });
    }

}
